import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

const REQUIRED_CONTRACTOR_AGREEMENTS = [
  'not_employer', 'supervising', 'liability', 'no_tradepower_liability',
];

const REQUIRED_WORKER_AGREEMENTS = [
  'independent_contractor', 'marketplace_only', 'taxes',
  'tools', 'work_authorized', 'liability',
];

const TRADES = [
  'All Trades', 'Electrical', 'Plumbing', 'HVAC', 'Roofing', 'Carpentry',
  'Welding', 'Painting', 'Concrete', 'Drywall', 'Pipefitting', 'General Labor', 'Landscaping',
];

const INDUSTRIES = ['All Industries', 'Residential', 'Commercial', 'Industrial', 'Service/Maintenance'];
const LEVELS = ['All Levels', 'Helper', 'Apprentice', 'Journeyman', 'Master'];
const LEVEL_ORDER = { 'Helper': 1, 'Apprentice': 2, 'Journeyman': 3, 'Master': 4 };

const TRADE_SPECIALIZATIONS = {
  'Electrical': ['Low Voltage (LV)', 'Medium Voltage (MV)', 'High Voltage (HV)', 'Data/Structured Cabling', 'Fire Alarm', 'Security Systems', 'Audio Visual', 'Solar/Renewable Energy', 'EV Charging', 'Controls/PLC', 'Instrumentation', 'Lighting Controls'],
  'Plumbing': ['Water/Supply', 'Drain/Waste/Vent', 'Gas Piping', 'Backflow Prevention', 'Medical Gas', 'Fire Suppression', 'Hydronic Heating'],
  'HVAC': ['Sheet Metal', 'Refrigeration', 'Building Controls/BMS', 'Ductless Mini Split', 'Commercial Rooftop Units', 'Industrial Process Cooling'],
  'Roofing': ['Shingle', 'Metal Roofing', 'Flat/TPO/EPDM', 'Tile', 'Repair/Maintenance'],
  'Carpentry': ['Framing', 'Finish Carpentry', 'Formwork/Concrete Forming', 'Cabinet Installation', 'Millwork'],
  'Welding': ['Stick/SMAW', 'MIG/GMAW', 'TIG/GTAW', 'Structural', 'Pipe Welding'],
  'Painting': ['Interior', 'Exterior', 'Commercial', 'Residential', 'Industrial Coatings'],
  'Concrete': ['Flatwork', 'Foundations', 'Decorative Concrete', 'Formwork', 'Pumping'],
  'Drywall': ['Hanging', 'Finishing/Taping', 'Metal Framing', 'Acoustical Ceilings', 'Repair'],
  'Pipefitting': ['Steam', 'Process Piping', 'HVAC Piping', 'Gas Piping', 'Hydronics'],
  'General Labor': ['Site Cleanup', 'Material Handling', 'Demolition', 'Concrete Labor', 'Flagging/Traffic Control'],
  'Landscaping': ['Lawn Care', 'Hardscaping', 'Irrigation', 'Tree Work', 'Snow Removal'],
};

const getResponseTime = (avgMinutes) => {
  if (avgMinutes === undefined || avgMinutes === null) return null;
  if (avgMinutes <= 60) return '⚡ Responds within 1 hour';
  const hours = Math.round(avgMinutes / 60);
  if (hours <= 12) return `⚡ Responds within ${hours} hour${hours > 1 ? 's' : ''}`;
  const days = Math.round(avgMinutes / (60 * 24));
  return `🕐 Responds within ${days} day${days > 1 ? 's' : ''}`;
};

const workerAgreementsComplete = (worker) => {
  if (!worker.agreements) return false;
  return REQUIRED_WORKER_AGREEMENTS.every(id => worker.agreements[id] === true);
};

function BrowseWorkers() {
  const [workers, setWorkers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [bookedWorkerIds, setBookedWorkerIds] = useState([]);
  const [contractorAgreementsComplete, setContractorAgreementsComplete] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState('All Trades');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [selectedIndustry, setSelectedIndustry] = useState('All Industries');
  const [selectedSpec, setSelectedSpec] = useState('All Specializations');
  const [licensedOnly, setLicensedOnly] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeSaturdays, setIncludeSaturdays] = useState(false);
  const [includeSundays, setIncludeSundays] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const availableSpecs = selectedTrade !== 'All Trades'
    ? TRADE_SPECIALIZATIONS[selectedTrade] || []
    : [];

  // Fix for mobile — wait for auth to initialize
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  // Check contractor agreements
  useEffect(() => {
    if (!user) return;
    const checkAgreements = async () => {
      try {
        const snap = await getDoc(doc(db, 'contractorProfiles', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          const allComplete = REQUIRED_CONTRACTOR_AGREEMENTS.every(
            id => data.agreements?.[id] === true
          );
          setContractorAgreementsComplete(allComplete);
        } else {
          setContractorAgreementsComplete(false);
        }
      } catch (err) {
        console.error(err);
        setContractorAgreementsComplete(false);
      }
    };
    checkAgreements();
  }, [user]);

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'workerProfiles'));
        const workerList = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const compliantWorkers = workerList.filter(w => workerAgreementsComplete(w));
        setWorkers(compliantWorkers);
        setFiltered(compliantWorkers);
      } catch (err) {
        console.error('Error fetching workers:', err);
      }
      setLoading(false);
    };
    fetchWorkers();
  }, []);

  useEffect(() => {
    const findBookedWorkers = async () => {
      if (!startDate || !endDate) { setBookedWorkerIds([]); return; }
      try {
        const q = query(collection(db, 'jobRequests'), where('status', '==', 'accepted'));
        const snapshot = await getDocs(q);
        const acceptedJobs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const booked = new Set();
        acceptedJobs.forEach(job => {
          if (!job.jobDate || !job.jobEndDate) return;
          const reqStart = new Date(startDate + 'T00:00:00');
          const reqEnd = new Date(endDate + 'T00:00:00');
          const jobStart = new Date(job.jobDate + 'T00:00:00');
          const jobEnd = new Date(job.jobEndDate + 'T00:00:00');
          if (reqStart > jobEnd || reqEnd < jobStart) return;
          const current = new Date(Math.max(reqStart, jobStart));
          const overlapEnd = new Date(Math.min(reqEnd, jobEnd));
          while (current <= overlapEnd) {
            const day = current.getDay();
            const isSat = day === 6;
            const isSun = day === 0;
            const isWeekday = day >= 1 && day <= 5;
            const reqWorksThisDay = isWeekday || (isSat && includeSaturdays) || (isSun && includeSundays);
            const jobWorksThisDay = isWeekday || (isSat && job.includeSaturdays) || (isSun && job.includeSundays);
            if (reqWorksThisDay && jobWorksThisDay) { booked.add(job.workerId); break; }
            current.setDate(current.getDate() + 1);
          }
        });
        setBookedWorkerIds([...booked]);
      } catch (err) { console.error(err); }
    };
    findBookedWorkers();
  }, [startDate, endDate, includeSaturdays, includeSundays]);

  useEffect(() => {
    let results = workers;
    if (selectedTrade !== 'All Trades') {
      results = results.filter(w =>
        w.tradeProfiles?.some(tp => tp.trade === selectedTrade) ||
        w.trades?.includes(selectedTrade)
      );
    }
    if (selectedLevel !== 'All Levels') {
      const requiredOrder = LEVEL_ORDER[selectedLevel] || 1;
      results = results.filter(w => {
        const profiles = w.tradeProfiles || [];
        if (profiles.length === 0) return true;
        return profiles.some(tp => {
          const tradeMatch = selectedTrade === 'All Trades' || tp.trade === selectedTrade;
          const levelOrder = LEVEL_ORDER[tp.level] || 1;
          return tradeMatch && levelOrder >= requiredOrder;
        });
      });
    }
    if (selectedIndustry !== 'All Industries') {
      results = results.filter(w => {
        const profiles = w.tradeProfiles || [];
        if (profiles.length === 0) return w.workTypes?.includes(selectedIndustry);
        return profiles.some(tp => {
          const tradeMatch = selectedTrade === 'All Trades' || tp.trade === selectedTrade;
          return tradeMatch && tp.industries?.includes(selectedIndustry);
        });
      });
    }
    if (selectedSpec !== 'All Specializations') {
      results = results.filter(w =>
        w.tradeProfiles?.some(tp => {
          const tradeMatch = selectedTrade === 'All Trades' || tp.trade === selectedTrade;
          return tradeMatch && tp.specializations?.includes(selectedSpec);
        })
      );
    }
    if (licensedOnly) {
      results = results.filter(w =>
        w.tradeProfiles?.some(tp => {
          const tradeMatch = selectedTrade === 'All Trades' || tp.trade === selectedTrade;
          return tradeMatch && tp.licenseStatus === 'verified';
        })
      );
    }
    if (zipCode.length === 5) {
      results = results.filter(w => w.zipCode === zipCode);
    }
    if (startDate && endDate) {
      results = results.filter(w => !bookedWorkerIds.includes(w.id));
      results = results.filter(w => {
        if (!w.blockedDays && !w.blockedDates) return true;
        const reqStart = new Date(startDate + 'T00:00:00');
        const reqEnd = new Date(endDate + 'T00:00:00');
        const current = new Date(reqStart);
        while (current <= reqEnd) {
          const dayOfWeek = current.getDay();
          const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][dayOfWeek];
          const dateStr = current.toISOString().split('T')[0];
          const isRequestedDay = (dayOfWeek >= 1 && dayOfWeek <= 5)
            || (dayOfWeek === 6 && includeSaturdays)
            || (dayOfWeek === 0 && includeSundays);
          if (isRequestedDay) {
            if (w.blockedDays && w.blockedDays.includes(dayName)) return false;
            if (w.blockedDates && w.blockedDates.find(b => b.date === dateStr)) return false;
          }
          current.setDate(current.getDate() + 1);
        }
        return true;
      });
    }
    setFiltered(results);
  }, [selectedTrade, selectedLevel, selectedIndustry, selectedSpec, licensedOnly, zipCode, workers, bookedWorkerIds, startDate, endDate, includeSaturdays, includeSundays]);

  const clearFilters = () => {
    setSelectedTrade('All Trades');
    setSelectedLevel('All Levels');
    setSelectedIndustry('All Industries');
    setSelectedSpec('All Specializations');
    setLicensedOnly(false);
    setZipCode('');
    setStartDate('');
    setEndDate('');
    setIncludeSaturdays(false);
    setIncludeSundays(false);
  };

  const getWorkerDisplayTrades = (worker) => {
    if (worker.tradeProfiles?.length > 0) {
      return worker.tradeProfiles.map(tp => ({
        trade: tp.trade, level: tp.level, years: tp.yearsExperience,
        industries: tp.industries || [], specializations: tp.specializations || [],
        licenseStatus: tp.licenseStatus, requiresLicense: tp.requiresLicense,
      }));
    }
    return (worker.trades || []).map(t => ({
      trade: t, level: null, years: null,
      industries: [], specializations: [], licenseStatus: 'none',
    }));
  };

  const ReliabilityBadge = ({ worker }) => {
    if (worker.noShows > 0 || worker.lateCancellations > 0) {
      return (
        <div style={styles.reliabilityBadgeWarn}>
          ⚠️ {[
            worker.lateCancellations > 0 && `${worker.lateCancellations} late cancel${worker.lateCancellations > 1 ? 's' : ''}`,
            worker.noShows > 0 && `${worker.noShows} no-show${worker.noShows > 1 ? 's' : ''}`
          ].filter(Boolean).join(' • ')}
        </div>
      );
    }
    return <div style={styles.reliabilityBadgeGood}>✅ Reliable</div>;
  };

  const StarRating = ({ rating, count }) => (
    <div style={styles.ratingContainer}>
      {[1,2,3,4,5].map(star => (
        <span key={star} style={{ color: star <= Math.round(rating) ? '#FFB800' : '#ddd', fontSize: '14px' }}>★</span>
      ))}
      <span style={styles.ratingCount}>
        {rating > 0 ? `${rating.toFixed(1)} (${count} reviews)` : 'No reviews yet'}
      </span>
    </div>
  );

  if (contractorAgreementsComplete === false) {
    return (
      <div style={styles.container}>
        <div style={styles.navbar}>
          <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
          <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
        </div>
        <div style={styles.content}>
          <div style={styles.agreementsGateCard}>
            <div style={styles.agreementsGateIcon}>🔒</div>
            <h2 style={styles.agreementsGateTitle}>Complete Your Profile Before Browsing Workers</h2>
            <p style={styles.agreementsGateText}>
              Before you can search for and hire workers on TradePower, you must complete
              your contractor profile and acknowledge all required platform agreements.
              This protects both you and the workers you engage.
            </p>
            <div style={styles.agreementsGateList}>
              <p style={styles.agreementsGateListTitle}>Required agreements include:</p>
              <div style={styles.agreementsGateItems}>
                <span style={styles.agreementsGateItem}>✓ TradePower marketplace acknowledgment</span>
                <span style={styles.agreementsGateItem}>✓ Supervisor responsibility confirmation</span>
                <span style={styles.agreementsGateItem}>✓ Job site liability acceptance</span>
                <span style={styles.agreementsGateItem}>✓ Platform liability disclaimer</span>
              </div>
            </div>
            <button style={styles.agreementsGateBtn} onClick={() => navigate('/contractor-profile')}>
              Complete My Profile & Agreements →
            </button>
            <button style={styles.agreementsGateBtnSecondary} onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>Find Skilled Workers</h2>
        <p style={styles.subtitle}>Browse licensed tradespeople available in your area</p>

        <div style={styles.filterCard}>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Trade</label>
              <select style={styles.filterSelect} value={selectedTrade}
                onChange={e => { setSelectedTrade(e.target.value); setSelectedSpec('All Specializations'); }}>
                {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Min Level</label>
              <select style={styles.filterSelect} value={selectedLevel}
                onChange={e => setSelectedLevel(e.target.value)}>
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Industry</label>
              <select style={styles.filterSelect} value={selectedIndustry}
                onChange={e => setSelectedIndustry(e.target.value)}>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            {availableSpecs.length > 0 && (
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Specialization</label>
                <select style={styles.filterSelect} value={selectedSpec}
                  onChange={e => setSelectedSpec(e.target.value)}>
                  <option value="All Specializations">All Specializations</option>
                  {availableSpecs.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>&nbsp;</label>
              <button style={styles.clearButton} onClick={clearFilters}>Clear All</button>
            </div>
          </div>

          <div style={{ ...styles.filterRow, marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Start Date</label>
              <input style={styles.filterInput} type="date" value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  if (!endDate || endDate < e.target.value) setEndDate(e.target.value);
                }}
                min={new Date().toISOString().split('T')[0]} />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>End Date</label>
              <input style={styles.filterInput} type="date" value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={startDate || new Date().toISOString().split('T')[0]} />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>ZIP Code</label>
              <input style={styles.filterInput} type="text" placeholder="28209"
                maxLength={5} value={zipCode} onChange={e => setZipCode(e.target.value)} />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Licensed Contractors</label>
              <button
                style={licensedOnly ? styles.licensedBtnActive : styles.licensedBtn}
                onClick={() => setLicensedOnly(!licensedOnly)}>
                {licensedOnly ? '✅ Licensed Only' : '○ Licensed Only'}
              </button>
            </div>
          </div>

          {startDate && endDate && (
            <div style={styles.weekendRow}>
              <span style={styles.weekendLabel}>Include weekend days:</span>
              <label style={styles.checkLabel}>
                <input type="checkbox" checked={includeSaturdays}
                  onChange={e => setIncludeSaturdays(e.target.checked)}
                  style={{ accentColor: '#E94560' }} />
                Saturdays
              </label>
              <label style={styles.checkLabel}>
                <input type="checkbox" checked={includeSundays}
                  onChange={e => setIncludeSundays(e.target.checked)}
                  style={{ accentColor: '#E94560' }} />
                Sundays
              </label>
            </div>
          )}

          <div style={styles.filterSummaryRow}>
            {!startDate || !endDate ? (
              <div style={styles.dateRequiredBanner}>
                📅 Select start and end dates to see available workers and send job requests
              </div>
            ) : (
              <div style={styles.filterSummary}>
                📅 Available {startDate === endDate
                  ? `on ${new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
                  : `${new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to ${new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                {bookedWorkerIds.length > 0 && (
                  <span style={styles.hiddenCount}> • {bookedWorkerIds.length} hidden (booked)</span>
                )}
              </div>
            )}
            {licensedOnly && (
              <div style={styles.licensedFilterBadge}>🏛️ Licensed Contractors Only</div>
            )}
            {selectedLevel !== 'All Levels' && (
              <div style={styles.levelFilterBadge}>👷 {selectedLevel} and above</div>
            )}
          </div>
        </div>

        <p style={styles.resultsCount}>
          {loading ? 'Loading workers...' : `${filtered.length} worker${filtered.length !== 1 ? 's' : ''} found`}
        </p>

        {loading ? (
          <div style={styles.loadingContainer}><p>Loading workers...</p></div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyIcon}>👷</p>
            <p style={styles.emptyTitle}>No workers found</p>
            <p style={styles.emptyText}>
              {startDate && endDate
                ? 'No workers match your filters for those dates. Try adjusting your search.'
                : 'Try adjusting your filters or check back later.'}
            </p>
          </div>
        ) : (
          <div style={styles.grid}>
            {filtered.map(worker => {
              const displayTrades = getWorkerDisplayTrades(worker);
              const relevantTrades = selectedTrade !== 'All Trades'
                ? displayTrades.filter(tp => tp.trade === selectedTrade)
                : displayTrades;
              return (
                <div key={worker.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    {worker.photoData ? (
                      <img src={worker.photoData} alt={worker.name} style={styles.avatar} />
                    ) : (
                      <div style={styles.avatarPlaceholder}>
                        <span style={styles.avatarIcon}>👤</span>
                      </div>
                    )}
                    <div style={styles.workerInfo}>
                      <h3 style={styles.workerName}>{worker.name}</h3>
                      <ReliabilityBadge worker={worker} />
                      {getResponseTime(worker.avgResponseMinutes) && (
                        <div style={styles.responseTimeBadge}>
                          {getResponseTime(worker.avgResponseMinutes)}
                        </div>
                      )}
                      <StarRating rating={worker.rating || 0} count={worker.reviewCount || 0} />
                      <div style={styles.locationRow}>
                        <span style={styles.locationText}>
                          📍 {worker.zipCode} • Within {worker.radius} miles
                        </span>
                      </div>
                    </div>
                    <div style={styles.rateTag}>
                      <span style={styles.rate}>${Math.ceil(worker.hourlyRate * 1.15)}</span>
                      <span style={styles.perHour}>/hr</span>
                    </div>
                  </div>

                  {relevantTrades.length > 0 && (
                    <div style={styles.tradeProfilesSection}>
                      {relevantTrades.slice(0, 2).map((tp, idx) => (
                        <div key={idx} style={styles.tradeProfileRow}>
                          <div style={styles.tradeProfileLeft}>
                            <span style={styles.tradeProfileName}>{tp.trade}</span>
                            {tp.level && (
                              <span style={styles.tradeProfileLevel}>{tp.level} • {tp.years} yrs</span>
                            )}
                            {tp.industries.length > 0 && (
                              <span style={styles.tradeProfileIndustry}>{tp.industries.join(' • ')}</span>
                            )}
                          </div>
                          <div style={styles.tradeProfileBadges}>
                            {tp.licenseStatus === 'verified' && (
                              <span style={styles.licensedBadge}>🏛️ Licensed</span>
                            )}
                            {tp.licenseStatus === 'pending' && (
                              <span style={styles.pendingBadge}>⏳ Pending</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {relevantTrades.length > 2 && (
                        <p style={styles.moreTrades}>+{relevantTrades.length - 2} more trade{relevantTrades.length - 2 > 1 ? 's' : ''}</p>
                      )}
                    </div>
                  )}

                  {selectedSpec !== 'All Specializations' && (
                    <div style={styles.specHighlight}>🔧 {selectedSpec}</div>
                  )}

                  {worker.certifications?.length > 0 && (
                    <div style={styles.certsRow}>
                      {worker.certifications.slice(0, 3).map(cert => (
                        <span key={cert} style={styles.certBadge}>{cert}</span>
                      ))}
                      {worker.certifications.length > 3 && (
                        <span style={styles.certBadge}>+{worker.certifications.length - 3} more</span>
                      )}
                    </div>
                  )}

                  {worker.bio && <p style={styles.bio}>{worker.bio}</p>}

                  {startDate && endDate && (
                    <div style={styles.availableBadge}>✅ Available for your dates</div>
                  )}

                  <button
                    style={startDate && endDate ? styles.hireButton : styles.hireButtonDisabled}
                    onClick={() => {
                      if (!startDate || !endDate) return;
                      navigate(`/worker/${worker.id}?start=${startDate}&end=${endDate}&sat=${includeSaturdays}&sun=${includeSundays}`);
                    }}>
                    {startDate && endDate ? 'View Profile & Request' : '📅 Select Dates First'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  navbar: {
    backgroundColor: 'white', padding: '16px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    touchAction: 'manipulation',
  },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' },
  title: { fontSize: '26px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666', marginBottom: '24px' },
  agreementsGateCard: {
    backgroundColor: 'white', borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)', padding: '40px 32px',
    maxWidth: '600px', margin: '0 auto', textAlign: 'center',
    border: '2px solid #E94560',
  },
  agreementsGateIcon: { fontSize: '56px', marginBottom: '20px' },
  agreementsGateTitle: { fontSize: '22px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '16px' },
  agreementsGateText: { fontSize: '14px', color: '#555', lineHeight: '1.7', marginBottom: '24px' },
  agreementsGateList: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', marginBottom: '28px', textAlign: 'left' },
  agreementsGateListTitle: { fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '10px' },
  agreementsGateItems: { display: 'flex', flexDirection: 'column', gap: '6px' },
  agreementsGateItem: { fontSize: '13px', color: '#333' },
  agreementsGateBtn: { display: 'block', width: '100%', padding: '14px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px', touchAction: 'manipulation' },
  agreementsGateBtnSecondary: { display: 'block', width: '100%', padding: '12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', touchAction: 'manipulation' },
  filterCard: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px' },
  filterRow: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  filterLabel: { fontSize: '12px', fontWeight: 'bold', color: '#555', textTransform: 'uppercase' },
  filterSelect: { padding: '9px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', backgroundColor: 'white', minWidth: '140px', cursor: 'pointer' },
  filterInput: { padding: '9px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', width: '130px' },
  clearButton: { padding: '9px 16px', backgroundColor: 'transparent', color: '#E94560', border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', touchAction: 'manipulation' },
  licensedBtn: { padding: '9px 14px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap', touchAction: 'manipulation' },
  licensedBtnActive: { padding: '9px 14px', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #28a745', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap', touchAction: 'manipulation' },
  weekendRow: { display: 'flex', alignItems: 'center', gap: '16px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' },
  weekendLabel: { fontSize: '13px', fontWeight: 'bold', color: '#555' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#333', cursor: 'pointer' },
  filterSummaryRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' },
  dateRequiredBanner: { fontSize: '13px', color: '#856404', backgroundColor: '#fff3cd', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ffc107' },
  filterSummary: { fontSize: '13px', color: '#555', backgroundColor: '#f8f9fa', padding: '8px 12px', borderRadius: '6px' },
  hiddenCount: { color: '#E94560', fontWeight: 'bold' },
  licensedFilterBadge: { fontSize: '13px', color: '#155724', backgroundColor: '#d4edda', padding: '8px 12px', borderRadius: '6px', fontWeight: 'bold' },
  levelFilterBadge: { fontSize: '13px', color: '#2c5282', backgroundColor: '#e8f4fd', padding: '8px 12px', borderRadius: '6px', fontWeight: 'bold' },
  resultsCount: { color: '#666', fontSize: '14px', marginBottom: '16px' },
  loadingContainer: { textAlign: 'center', padding: '60px', color: '#666' },
  emptyState: { textAlign: 'center', padding: '60px' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyTitle: { fontSize: '20px', color: '#1a1a2e', marginBottom: '8px' },
  emptyText: { color: '#666', fontSize: '14px', maxWidth: '400px', margin: '0 auto' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' },
  card: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  avatar: { width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #E94560', flexShrink: 0 },
  avatarPlaceholder: { width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  avatarIcon: { fontSize: '28px' },
  workerInfo: { flex: 1 },
  workerName: { fontSize: '17px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '3px' },
  reliabilityBadgeGood: { backgroundColor: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', display: 'inline-block', marginBottom: '4px' },
  reliabilityBadgeWarn: { backgroundColor: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', display: 'inline-block', marginBottom: '4px' },
  responseTimeBadge: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', display: 'inline-block', marginBottom: '4px' },
  ratingContainer: { display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '4px' },
  ratingCount: { fontSize: '12px', color: '#999', marginLeft: '4px' },
  locationRow: { marginTop: '2px' },
  locationText: { fontSize: '12px', color: '#999' },
  rateTag: { display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#fff0f3', borderRadius: '8px', padding: '8px 12px', flexShrink: 0 },
  rate: { fontSize: '20px', fontWeight: 'bold', color: '#E94560' },
  perHour: { fontSize: '11px', color: '#999' },
  tradeProfilesSection: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  tradeProfileRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  tradeProfileLeft: { display: 'flex', flexDirection: 'column', gap: '1px' },
  tradeProfileName: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e' },
  tradeProfileLevel: { fontSize: '12px', color: '#666' },
  tradeProfileIndustry: { fontSize: '11px', color: '#999' },
  tradeProfileBadges: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  licensedBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' },
  pendingBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' },
  moreTrades: { fontSize: '12px', color: '#999', margin: 0 },
  specHighlight: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' },
  certsRow: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  certBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' },
  bio: { fontSize: '13px', color: '#666', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  availableBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', textAlign: 'center' },
  hireButton: { padding: '12px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', marginTop: '4px', touchAction: 'manipulation' },
  hireButtonDisabled: { padding: '12px', backgroundColor: '#f0f0f0', color: '#999', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'not-allowed', marginTop: '4px' },
};

export default BrowseWorkers;